import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, PencilSimple, Trash, Tag, X, Star } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyCat = {
  name: '', name_hi: '', slug: '', parent_id: '', description: '',
  icon: '', banner: '', seo_title: '', seo_description: '', is_featured: false,
};

export default function CmsCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | category object

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/cms/categories`, { withCredentials: true });
      setItems(data.items);
    } catch (e) { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing === 'new' || !form.category_id) {
        await axios.post(`${API}/api/admin/cms/categories`, form, { withCredentials: true });
        toast.success('Category created');
      } else {
        await axios.patch(`${API}/api/admin/cms/categories/${form.category_id}`, form, { withCredentials: true });
        toast.success('Category updated');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API}/api/admin/cms/categories/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="cms-categories">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Categories</h2>
          <p className="text-sm text-muted-foreground">{items.length} categories</p>
        </div>
        <button
          onClick={() => setEditing('new')}
          data-testid="cms-categories-new"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium hover:opacity-90"
        >
          <Plus size={16} /> New Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          <p className="col-span-full text-center py-10 text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <Tag size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No categories yet. Create your first one.</p>
          </div>
        ) : items.map((c) => (
          <div key={c.category_id} className="bg-card border border-border rounded-lg p-4" data-testid={`cat-item-${c.category_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{c.name}</p>
                  {c.is_featured && <Star size={14} className="text-amber-500" weight="fill" />}
                </div>
                {c.name_hi && <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Noto Sans Devanagari, sans-serif' }}>{c.name_hi}</p>}
                <p className="text-xs text-muted-foreground mt-1">/{c.slug}</p>
                {c.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-muted rounded-lg" data-testid={`cat-edit-${c.category_id}`}>
                  <PencilSimple size={14} />
                </button>
                <button onClick={() => remove(c.category_id)} className="p-1.5 hover:bg-muted rounded-lg text-red-600" data-testid={`cat-delete-${c.category_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <CategoryForm
          category={editing === 'new' ? emptyCat : { ...emptyCat, ...editing }}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function CategoryForm({ category, onClose, onSave }) {
  const [form, setForm] = useState(category);
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  return (
    <>
      <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border z-50 overflow-y-auto" data-testid="cat-form">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {category.category_id ? 'Edit Category' : 'New Category'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Name (English)" required>
            <input required value={form.name} onChange={set('name')} data-testid="cat-name" className="input" />
          </Field>
          <Field label="Name (Hindi)">
            <input value={form.name_hi || ''} onChange={set('name_hi')} data-testid="cat-name-hi" className="input" style={{ fontFamily: 'Noto Sans Devanagari, sans-serif' }} />
          </Field>
          <Field label="Slug (auto if blank)">
            <input value={form.slug || ''} onChange={set('slug')} data-testid="cat-slug" className="input" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={form.description || ''} onChange={set('description')} data-testid="cat-description" className="input" />
          </Field>
          <Field label="Icon URL">
            <input value={form.icon || ''} onChange={set('icon')} className="input" />
          </Field>
          <Field label="Banner URL">
            <input value={form.banner || ''} onChange={set('banner')} className="input" />
          </Field>
          <Field label="SEO Title">
            <input value={form.seo_title || ''} onChange={set('seo_title')} className="input" />
          </Field>
          <Field label="SEO Description">
            <textarea rows={2} value={form.seo_description || ''} onChange={set('seo_description')} className="input" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_featured} onChange={set('is_featured')} data-testid="cat-featured" />
            Featured
          </label>
          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-full text-sm">Cancel</button>
            <button type="submit" data-testid="cat-save" className="flex-1 bg-primary text-primary-foreground rounded-full py-2 text-sm font-semibold hover:opacity-90">Save</button>
          </div>
          <style>{`.input{width:100%;padding:0.5rem 0.75rem;background:hsl(var(--muted)/0.4);border:1px solid transparent;border-radius:0.5rem;font-size:0.875rem;outline:none}.input:focus{border-color:hsl(var(--primary));background:hsl(var(--background))}`}</style>
        </form>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
      {children}
    </div>
  );
}
